import { call, put, SagaGenerator } from 'typed-redux-saga';

import { SELF } from '../../../../../shared/constants';
import { RoomAgentConfig } from '../../../../../shared/features/config';
import { GoloomSDK } from '../../../../../shared/features/goloom-sdk/goloom-sdk';
import { rootRoomAgentSaga } from '../../../../../shared/features/room-agent/sagas';
import { SDKAdapter } from '../../../../../shared/features/room-agent/types';
import { ChannelHolder } from '../../../../../shared/helpers/channelHolder';
import { createLogger } from '../../../../../shared/helpers/loggerFacade';
import { recordHelpers } from '../../../sagas/root';
import {
    disconnectConferenceAction,
    disconnectedFromConferenceDoneAction,
    updateParticipant,
} from '../../conference/slice';

const logger = createLogger('goloom:room-agent');

/**
 * @param sdk - Адаптер к GoloomSDK
 * @param roomAgentConfig - конфиг от рум-агента
 */
export function* initRoomAgentSaga(sdk: GoloomSDK, roomAgentConfig: RoomAgentConfig): SagaGenerator<void> {
    const channelHolder = new ChannelHolder();
    logger.debug('GOLOOM ROOM AGENT SAGA');
    try {
        const adapter: SDKAdapter = {
            setLocalTrack: function* (track: MediaStreamTrack) {
                const localTrack = yield* call([sdk, sdk.replaceLocalTrack], track, true);
                if (localTrack) {
                    yield put(
                        updateParticipant({
                            id: SELF,
                            [localTrack.kind]: {
                                id: localTrack.id,
                                muted: false,
                            },
                        })
                    );
                }
            },
            getRemoteTracks: () => {
                return sdk.getRemoteAudioTracks();
            },
            disconnectDoneAction: disconnectedFromConferenceDoneAction,
            cancel: function* () {
                yield put(disconnectConferenceAction());
            },
            addTrackChannel: channelHolder.getChannelForEvent(sdk.onAudioTracksAdded),
            removeTrackChannel: channelHolder.getChannelForEvent(sdk.onAudioTracksRemoved),
            onIceCandidate: (candidate: RTCIceCandidate) => {
                const webrtcIceCandidate = {
                    pcSeq: 0,
                    target: 'TARGET_UNSPECIFIED',
                    candidate: candidate.candidate,
                    sdpMid: candidate.sdpMid || undefined,
                    sdpMlineIndex: candidate.sdpMLineIndex || undefined,
                    usernameFragment: candidate.usernameFragment || undefined,
                } as const;

                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore Пока так. Goloom должен экспортнуть enum WebrtcIceCandidate_Target
                sdk.session?.sendRoomAgentSignalingMessage({ webrtcIceCandidate });
            },
            onRtcStats: (report: RTCStatsReport) => {
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore. RTCStatsReport – это iterable
                sdk.session?.sendRoomAgentRtcStats([...report.values()]);
            },
            onSdp: (description) => {
                const message =
                    description.type === 'offer' ? { sdpOffer: description.sdp } : { sdpAnswer: description.sdp };

                sdk.session?.sendRoomAgentSignalingMessage(message);
            },
            onConnectionState: (state: RTCPeerConnectionState) => {
                sdk.session?.sendRoomAgentSignalingMessage({
                    roomAgentPeerConnectionEvent: { eventName: 'connectionStateChanged', state },
                });
            },
            onSignalingState: (state: RTCSignalingState) => {
                sdk.session?.sendRoomAgentSignalingMessage({
                    roomAgentPeerConnectionEvent: { eventName: 'signalingStateChanged', state },
                });
            },
        };

        yield* call(rootRoomAgentSaga, adapter, recordHelpers, roomAgentConfig.debug);
    } finally {
        logger.debug('close room-agent');

        channelHolder.close();
    }
}
